import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { useBusiness } from '../context/BusinessContext';
import { api } from '../api/client';
import { money, date } from '../lib/format';

const todayStr = () => new Date().toISOString().slice(0, 10);
const yearStart = () => `${new Date().getFullYear()}-01-01`;

const TYPES = [
  { key: 'pnl', label: 'Profit & Loss', range: true },
  { key: 'balance-sheet', label: 'Balance Sheet', range: false },
  { key: 'ar-aging', label: 'A/R Aging', range: false },
  { key: 'sales-tax', label: 'Sales Tax', range: true },
];

export default function Reports() {
  const { activeId, activeBusiness } = useBusiness();
  const [type, setType] = useState('pnl');
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(todayStr());
  const [asOf, setAsOf] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cfg = TYPES.find((t) => t.key === type);

  const run = useCallback(async () => {
    setLoading(true); setError(null);
    const qs = cfg.range ? `from=${from}&to=${to}` : `as_of=${asOf}`;
    try {
      const d = await api(`/reports/${type}?business_id=${activeId}&${qs}`);
      setData({ ...d, _type: type });
    } catch (e) { setError(e?.detail || 'Could not run the report.'); setData(null); }
    finally { setLoading(false); }
  }, [type, activeId, from, to, asOf, cfg]);

  // Re-run when the report type or business changes (date changes use the Run button).
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [type, activeId]);

  const periodLabel = cfg.range ? `${date(from)} – ${date(to)}` : `As of ${date(asOf)}`;

  return (
    <>
      <PageHeader title="Reports" subtitle={activeBusiness?.name} />

      <section className="panel panel--pad no-print">
        <div className="rep-controls">
          <div className="field">
            <label>Report</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          {cfg.range ? (
            <>
              <div className="field"><label>From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
              <div className="field"><label>To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            </>
          ) : (
            <div className="field"><label>As of</label><input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} /></div>
          )}
          <div className="rep-controls__btns">
            <button className="btn btn--primary" onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run report'}</button>
            <button className="btn" onClick={() => window.print()} disabled={!data}>Print / Save PDF</button>
          </div>
        </div>
        {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}
      </section>

      {data && (
        <section className="panel panel--pad report-print">
          <div className="rep-head">
            <h2 className="rep-title">{cfg.label}</h2>
            <div className="rep-sub">{activeBusiness?.name} · {periodLabel}</div>
          </div>
          {data._type === 'pnl' && <PnL d={data} />}
          {data._type === 'balance-sheet' && <BalanceSheet d={data} />}
          {data._type === 'ar-aging' && <ArAging d={data} />}
          {data._type === 'sales-tax' && <SalesTax d={data} />}
        </section>
      )}
    </>
  );
}

const Row = ({ label, amount, cls = '' }) => (
  <div className={`rep-row ${cls}`}><span>{label}</span><span className="num">{money(amount)}</span></div>
);

function PnL({ d }) {
  return (
    <>
      <h3 className="rep-section">Income</h3>
      {d.income.length === 0 ? <p className="hint">No income in this period.</p> : d.income.map((a) => <Row key={a.code} label={`${a.code} · ${a.name}`} amount={a.amount} />)}
      <Row label="Total income" amount={d.total_income} cls="rep-row--total" />

      <h3 className="rep-section">Expenses</h3>
      {d.expense.length === 0 ? <p className="hint">No expenses in this period.</p> : d.expense.map((a) => <Row key={a.code} label={`${a.code} · ${a.name}`} amount={a.amount} />)}
      <Row label="Total expenses" amount={d.total_expense} cls="rep-row--total" />

      <Row label="Net income" amount={d.net_income} cls="rep-row--grand" />

      <h3 className="rep-section">Revenue by source</h3>
      <Row label="Contract" amount={d.revenue_by_source.contract} />
      <Row label="Non-contract" amount={d.revenue_by_source.non_contract} />
    </>
  );
}

function BalanceSheet({ d }) {
  return (
    <>
      <h3 className="rep-section">Assets</h3>
      {d.assets.map((a) => <Row key={a.code} label={`${a.code} · ${a.name}`} amount={a.amount} />)}
      <Row label="Total assets" amount={d.total_assets} cls="rep-row--total" />

      <h3 className="rep-section">Liabilities</h3>
      {d.liabilities.length === 0 ? <p className="hint">None.</p> : d.liabilities.map((a) => <Row key={a.code} label={`${a.code} · ${a.name}`} amount={a.amount} />)}
      <Row label="Total liabilities" amount={d.total_liabilities} cls="rep-row--total" />

      <h3 className="rep-section">Equity</h3>
      {d.equity.map((a) => <Row key={a.code} label={`${a.code} · ${a.name}`} amount={a.amount} />)}
      <Row label="Net income" amount={d.net_income} />
      <Row label="Total equity" amount={d.total_equity} cls="rep-row--total" />

      <Row label="Liabilities + equity" amount={d.total_liabilities + d.total_equity} cls="rep-row--grand" />
      {!d.balanced && <p className="form-error" style={{ marginTop: 10 }}>Out of balance by {money(d.out_of_balance)} — there may be unbalanced or manual entries to review.</p>}
    </>
  );
}

function ArAging({ d }) {
  if (!d.customers.length) return <p className="hint">No outstanding receivables as of this date.</p>;
  const cols = [['current', 'Current'], ['d30', '1–30'], ['d60', '31–60'], ['d90', '61–90'], ['d90p', '90+']];
  return (
    <table className="rep-table">
      <thead>
        <tr><th>Customer</th>{cols.map(([k, l]) => <th key={k} className="num">{l}</th>)}<th className="num">Total</th></tr>
      </thead>
      <tbody>
        {d.customers.map((c) => (
          <tr key={c.customer_id}>
            <td>{c.customer_name}</td>
            {cols.map(([k]) => <td key={k} className="num">{c[k] ? money(c[k]) : '—'}</td>)}
            <td className="num">{money(c.total)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr><td>Total</td>{cols.map(([k]) => <td key={k} className="num">{money(d.totals[k])}</td>)}<td className="num">{money(d.totals.total)}</td></tr>
      </tfoot>
    </table>
  );
}

function SalesTax({ d }) {
  return (
    <>
      <Row label={`Total sales (${d.invoice_count} invoice${d.invoice_count === 1 ? '' : 's'})`} amount={d.total_sales} />
      <Row label="Taxable sales" amount={d.taxable_sales} />
      <Row label="Non-taxable sales" amount={d.nontaxable_sales} />
      <Row label="Sales tax collected" amount={d.tax_collected} cls="rep-row--grand" />
    </>
  );
}
